import { localToUtc } from "./slotTime";
import React, { FC, useEffect, useRef, useState } from "react";
import { IInterviewDate } from "./helper";
import { FormInstance, Modal, Space } from "antd";
import { axiosInstant, endpoints } from "../../api";
import { IApplicant, IManager } from "../ManagersPanel/helper";
import { useSearchParams } from "react-router-dom";
import { useUser } from "../../context";
import { showError } from "../../Notifications";
import ScheduleMeetingForm from "./ScheduleMeetingForm";

interface Props {
  candidate: IApplicant;
  interviewDate: IInterviewDate;
  setInterviewDate: (e?: IInterviewDate) => void;
  mode?: 'manager' | 'candidate';
  jobApplicationId?: string;
  onScheduled?: () => void;
  selectedUserIds?: string[];
  job?: any;
  jobApplication?: any;
}

const ScheduleMeetingModal: FC<Props> = (props) => {
  const {
    interviewDate: { date, hour },
    setInterviewDate,
    candidate,
    mode = 'manager'
  } = props;

  const { user } = useUser();
  const formRef = useRef<FormInstance<any>>(null);
  const closeModal = () => setInterviewDate(undefined);
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [managers, setManaers] = useState<Array<IManager>>([]);
  const [selectedManager, setSelectedManager] = useState<IManager>();
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [jobPosition, setJobPosition] = useState<string | undefined>(
    props.job?.positionTitle || props.job?.title || props.candidate?.position
  );

  // The picked slot is in the viewer's zone; availability is looked up in UTC.
  const utcSlot = (() => {
    const u = localToUtc(date, hour, new Date().getFullYear());
    return { date: u.date, hour: u.hour, year: u.year };
  })();

  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (mode === 'manager' && jobId) {
      axiosInstant
        .post(endpoints.availableManagers, { jobId, ...utcSlot })
        .then((response) => {
          const { data } = response;
          setManaers(data);
          if (data.length) setSelectedManager(data[0]);
          setLoading(false);
        })
        .catch((err) => showError(err));
    }
  }, [date, hour, searchParams, mode]);

  useEffect(() => {
    if (mode === 'manager' && props.selectedUserIds && props.selectedUserIds.length) {
      axiosInstant.get('/users/by-ids', { params: { ids: props.selectedUserIds } })
        .then(res => setSelectedUsers(res.data));
    }
  }, [mode, props.selectedUserIds]);

  // Ensure loading is false for candidate mode
  useEffect(() => {
    if (mode === 'candidate') setLoading(false);
  }, [mode]);

  useEffect(() => {
    // Use jobApplication and job from props if available
    if (!jobPosition) {
      if (props.jobApplication && (props.jobApplication.jobId || props.jobApplication.job)) {
        const job = props.job || props.jobApplication.job;
        console.log('Using job from props:', job);
        setJobPosition(job?.positionTitle || job?.title || undefined);
        return;
      }
    }
    // Fetch from backend if still missing
    async function fetchJobPosition() {
      if (!jobPosition && props.jobApplicationId) {
        try {
          const jobAppRes = await axiosInstant.get(`/job-applications/${props.jobApplicationId}`);
          const jobApp = jobAppRes.data;
          console.log('Fetched job application:', jobApp);
          if (jobApp && jobApp.jobId) {
            const jobRes = await axiosInstant.get(`/jobs/${jobApp.jobId}`);
            const job = jobRes.data;
            console.log('Fetched job:', job);
            setJobPosition(job?.positionTitle || job?.title || undefined);
          } else {
            console.log('No jobId found in job application');
          }
        } catch (err) {
          console.log('Error fetching job application or job:', err);
          // fallback: leave as undefined
        }
      }
    }
    fetchJobPosition();
  }, [props.job, props.jobApplication, props.jobApplicationId, jobPosition]);

  const getHeading = (heading: string, data: IManager) => (
    <Space>
      <div style={{ fontSize: 14, fontWeight: "600" }}>{heading}:</div>
      <div style={{ fontSize: 14 }}>{data.name}</div>
      <span>
        (
        <a href={`mailto://${data.email}`} style={{ fontSize: 12 }}>
          {data.email}
        </a>
        )
      </span>
    </Space>
  );

  // The recruiter's "Reschedule" action on an already-scheduled interview opens
  // this same calendar with ?reschedule=true, so the form hits
  // /rescheduleCandidateInterview instead of /scheduleCandidateInterview - the
  // only difference, since the backend then also cancels the old calendar
  // event and clears its old reserved slot before creating the new one.
  const isReschedule = searchParams.get('reschedule') === 'true';

  const formProps = {
    ...props,
    loading,
    setLoading,
    formRef,
    selectedManager,
    mode,
    jobPosition,
    isReschedule,
    ...(mode === 'candidate' && { jobApplicationId: props.jobApplicationId }),
  };

  // Ensure loader is reset when modal is closed
  const handleCancel = () => {
    setLoading(false);
    closeModal();
  };

  // In candidate mode, after successful schedule:
  const handleOk = () => {
    formRef.current?.submit();
  };

  return (
    <Modal
      title="Schedule Interview"
      centered
      open
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
    >
      <div style={{ marginTop: 20 }}>
        {mode === 'manager' && managers.length === 1 && getHeading("Interviewer", managers[0])}
      </div>
      <div>{getHeading("Candidate", candidate)}</div>
      {mode === 'manager' && <div>{getHeading("Hosted By", user)}</div>}
      {mode === 'manager' && selectedUsers.length > 0 && (
        <div>
          <div style={{ fontWeight: 600 }}>Interviewers:</div>
          {selectedUsers
            .filter(u => u._id !== candidate._id)
            .map(u => (
              <div key={u._id}>
                {u.name} (<a href={`mailto:${u.email}`}>{u.email}</a>)
              </div>
            ))}
        </div>
      )}
      <ScheduleMeetingForm
        {...formProps}
        selectedUserIds={mode === 'manager' ? props.selectedUserIds : undefined}
        
      />
    </Modal>
  );
};

export default ScheduleMeetingModal;
